/**
 * Krok 5: Opinie klientów — ręczne dodawanie referencji.
 * Każda opinia: autor, treść, ocena (1–5 gwiazdek), data (opcjonalnie).
 *
 * UWAGA: To są opinie wstępne / referencyjne dodane przez właściciela.
 * Zweryfikowane opinie (wysyłane do leadów po 30 dniach) — będą w osobnym module.
 */
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Plus, Trash2, Star, MessageSquare, CheckCircle2 } from 'lucide-react';

function makeReview() {
  return { id: Math.random().toString(36).slice(2, 10), author: '', text: '', rating: 5 };
}

export default function Step5Reviews({ data, updateData }) {
  const reviews = data.reviews || [];

  const update = (idx, patch) => {
    const next = reviews.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    updateData({ reviews: next });
  };

  const add = () => updateData({ reviews: [...reviews, makeReview()] });
  const remove = (idx) => updateData({ reviews: reviews.filter((_, i) => i !== idx) });

  return (
    <div className="space-y-8" data-testid="business-wizard-v2-step-reviews">
      <header>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center">
            <CheckCircle2 className="w-5 h-5 text-orange-500" />
          </div>
          <span className="text-xs uppercase tracking-wider font-semibold text-orange-600">Krok 5 z 7</span>
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Co mówią o Tobie klienci?</h2>
        <p className="text-sm text-gray-500 mt-1">Dodaj opinie, które już masz (Google, Facebook, Messenger). Później zbieramy je automatycznie po każdym leadzie.</p>
      </header>

      {reviews.length === 0 ? (
        <div className="border-2 border-dashed border-orange-200 rounded-2xl p-8 text-center bg-orange-50/30">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-orange-100 flex items-center justify-center mb-3">
            <MessageSquare className="w-7 h-7 text-orange-500" />
          </div>
          <p className="font-semibold text-gray-900">Dodaj pierwszą opinię</p>
          <p className="text-sm text-gray-500 mt-1 mb-4">Wystarczą 2–3 referencje aby zbudować zaufanie.</p>
          <Button type="button" onClick={add} data-testid="bwv2-add-review-btn" className="bg-orange-500 hover:bg-orange-600">
            <Plus className="w-4 h-4 mr-1.5" /> Dodaj opinię
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((r, idx) => (
            <div key={r.id || idx} className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-5" data-testid={`bwv2-review-${idx}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-sm font-bold text-gray-900">
                  <span className="w-6 h-6 rounded-full bg-orange-100 text-orange-600 text-xs flex items-center justify-center">{idx + 1}</span>
                  Opinia #{idx + 1}
                </div>
                <button
                  type="button"
                  onClick={() => remove(idx)}
                  data-testid={`bwv2-remove-review-${idx}`}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">Autor</Label>
                    <Input
                      value={r.author || ''}
                      onChange={(e) => update(idx, { author: e.target.value })}
                      placeholder="np. Anna K."
                      data-testid={`bwv2-review-author-${idx}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">Ocena</Label>
                    <div className="flex items-center gap-1 h-10">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => update(idx, { rating: n })}
                          data-testid={`bwv2-review-star-${idx}-${n}`}
                          className="p-0.5"
                          aria-label={`${n} gwiazdek`}
                        >
                          <Star
                            className={`w-6 h-6 ${
                              n <= (Number(r.rating) || 0)
                                ? 'fill-amber-400 text-amber-400'
                                : 'text-gray-300'
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-gray-600 mb-1 block">Treść opinii *</Label>
                  <Textarea
                    value={r.text || ''}
                    onChange={(e) => update(idx, { text: e.target.value })}
                    placeholder="Świetna obsługa, polecam każdemu!"
                    rows={3}
                    maxLength={500}
                    data-testid={`bwv2-review-text-${idx}`}
                  />
                </div>
              </div>
            </div>
          ))}
          <Button
            type="button"
            onClick={add}
            data-testid="bwv2-add-review-btn"
            variant="outline"
            className="w-full border-dashed border-orange-300 text-orange-600 hover:bg-orange-50"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Dodaj kolejną opinię
          </Button>
        </div>
      )}
    </div>
  );
}
